import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Countdown from 'react-countdown';
import {
  Search,
  Lock,
  AlertCircle,
  Trophy,
  AlertOctagon,
  AlertTriangle,
  HeartPulse,
  Sun,
  Moon,
  ExternalLink,
} from 'lucide-react';

import { Button } from './components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from './components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
} from './components/ui/drawer';
import { Avatar, AvatarImage, AvatarFallback } from './components/ui/avatar';
import { Badge } from './components/ui/badge';
import { Skeleton } from './components/ui/skeleton';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from './components/ui/tabs';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from './components/ui/tooltip';
import { getDescription } from './data/playerDescriptions';
import { getTeamColors } from './data/teamColors';
import {
  getSeason,
  loadSeasonStats,
  getPlayerWeeks,
  summarizePlayer,
} from './lib/sleeperStats';
import { useMediaQuery } from './lib/useMediaQuery';
import { cn } from './lib/utils';

const SERVER_URL = 'https://kpr-svr.herokuapp.com';

const EXCLUDED_TEAMS = new Set(['lcushing', 'Kristi Kreme']);

const POS_COLORS = {
  QB: 'bg-pos-qb/15 text-pos-qb border-pos-qb/40',
  RB: 'bg-pos-rb/15 text-pos-rb border-pos-rb/40',
  WR: 'bg-pos-wr/15 text-pos-wr border-pos-wr/40',
  TE: 'bg-pos-te/15 text-pos-te border-pos-te/40',
  K: 'bg-pos-k/15 text-pos-k border-pos-k/40',
  DEF: 'bg-pos-def/15 text-pos-def border-pos-def/40',
};

// Sleeper roster status values that mean "on IR" without an injury_status set
const IR_STATUSES = new Set([
  'Injured Reserve',
  'Reserve',
  'IR',
  'PUP',
  'NFI',
]);

function getInjury(player) {
  const explicit = player.injuryStatus;
  if (explicit) {
    return {
      status: explicit,
      bodyPart: player.injuryBodyPart,
      notes: player.injuryNotes,
      startDate: player.injuryStartDate,
    };
  }
  if (player.status && IR_STATUSES.has(player.status)) {
    return {
      status: 'IR',
      bodyPart: player.injuryBodyPart,
      notes: player.injuryNotes,
      startDate: player.injuryStartDate,
    };
  }
  return null;
}

function injuryConfig(status) {
  switch (status) {
    case 'IR':
    case 'Out':
    case 'Suspended':
    case 'PUP':
    case 'NFI':
      return {
        Icon: status === 'IR' ? HeartPulse : AlertOctagon,
        color: 'text-red-400',
        label: status,
      };
    case 'Doubtful':
      return {
        Icon: AlertTriangle,
        color: 'text-orange-400',
        label: status,
      };
    case 'Questionable':
      return {
        Icon: AlertCircle,
        color: 'text-amber-300',
        label: status,
      };
    case 'Probable':
      return {
        Icon: AlertCircle,
        color: 'text-emerald-400',
        label: status,
      };
    default:
      return {
        Icon: AlertCircle,
        color: 'text-muted-foreground',
        label: status,
      };
  }
}

function InjuryIcon({ injury, size = 'sm' }) {
  if (!injury) return null;
  const cfg = injuryConfig(injury.status);
  const iconSize = size === 'lg' ? 'h-5 w-5' : 'h-3.5 w-3.5';
  const detail = [injury.bodyPart, injury.notes].filter(Boolean).join(' · ');
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn('inline-flex shrink-0 cursor-help', cfg.color)}
          aria-label={`Injury status: ${cfg.label}${
            detail ? ` (${detail})` : ''
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <cfg.Icon className={iconSize} />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <div className="space-y-0.5">
          <div className="font-semibold">{cfg.label}</div>
          {injury.bodyPart && (
            <div className="text-popover-foreground/80">
              {injury.bodyPart}
            </div>
          )}
          {injury.notes && (
            <div className="text-popover-foreground/70">{injury.notes}</div>
          )}
          {injury.startDate && (
            <div className="text-popover-foreground/60 text-[10px] uppercase tracking-wide">
              since {injury.startDate}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function TeamRing({ team, size = 'sm', children }) {
  const colors = getTeamColors(team);
  const dim = size === 'lg' ? 'h-24 w-24' : 'h-12 w-12';
  const pad = size === 'lg' ? 'p-[3px]' : 'p-[2px]';
  const outerR = size === 'lg' ? 'rounded-xl' : 'rounded-md';
  const baseClass = cn('shrink-0', dim, pad, outerR);

  if (!colors) {
    return <div className={cn(baseClass, 'bg-border')}>{children}</div>;
  }

  return (
    <div
      className={baseClass}
      style={{
        backgroundImage: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      }}
    >
      {children}
    </div>
  );
}

function playerPhotoUrl({ playerId, pos }) {
  if (!playerId) return null;
  if (pos === 'DEF') {
    return `https://sleepercdn.com/images/team_logos/nfl/${playerId.toLowerCase()}.png`;
  }
  return `https://sleepercdn.com/content/nfl/players/${playerId}.jpg`;
}

function initials(name) {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function PositionBadge({ pos, className }) {
  if (!pos || pos === 'D/ST') pos = 'DEF';
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-[5px] border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
        POS_COLORS[pos] ?? 'bg-muted text-muted-foreground border-border',
        className
      )}
    >
      {pos}
    </span>
  );
}

function TeamCardSkeleton() {
  return (
    <Card className="w-full">
      <CardHeader>
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 rounded-md" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-12" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ThemeToggle() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return localStorage.getItem('theme') ?? 'dark';
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    try {
      localStorage.setItem('theme', theme);
    } catch (_) {}
  }, [theme]);

  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setTheme(next)}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
    >
      {theme === 'dark' ? (
        <Sun className="h-4 w-4" />
      ) : (
        <Moon className="h-4 w-4" />
      )}
    </Button>
  );
}

function PlayerRow({ player, isKept, onSelect }) {
  const photo = playerPhotoUrl({ playerId: player.playerId, pos: player.pos });
  const injury = getInjury(player);
  return (
    <button
      type="button"
      onClick={() => onSelect(player)}
      className={cn(
        'group flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left transition-colors',
        'hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isKept && 'bg-primary/10 ring-1 ring-primary/40'
      )}
    >
      <TeamRing team={player.team} size="sm">
        <Avatar className="h-full w-full rounded-[9px]">
          {photo && (
            <AvatarImage
              src={photo}
              alt={player.name}
              className={
                player.pos === 'DEF' ? 'object-contain p-1.5' : undefined
              }
            />
          )}
          <AvatarFallback className="rounded-[9px]">
            {initials(player.name)}
          </AvatarFallback>
        </Avatar>
      </TeamRing>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium capitalize">
            {player.name}
          </span>
          {injury && <InjuryIcon injury={injury} />}
          {isKept && (
            <Lock className="h-3.5 w-3.5 shrink-0 text-primary" />
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <PositionBadge pos={player.pos} />
          <span>{player.team}</span>
        </div>
      </div>
      <span
        className={cn(
          'text-xs font-semibold tabular-nums',
          isKept ? 'text-primary' : 'text-muted-foreground'
        )}
      >
        Rd {player.keep}
      </span>
    </button>
  );
}

function TeamCard({ teamName, players, kept, onSelectPlayer }) {
  const keptName = kept?.[teamName]?.keeper;
  const sorted = useMemo(
    () =>
      players
        .filter((p) => p.pos !== 'K')
        .sort((a, b) => a.keep - b.keep),
    [players]
  );

  return (
    <Card className="flex w-full flex-col overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 border-b border-white/10 py-4">
        <CardTitle className="text-base">{teamName}</CardTitle>
        {keptName && (
          <Badge className="gap-1">
            <Lock className="h-3 w-3" />
            <span className="capitalize">{keptName}</span>
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-0.5 p-2">
        {sorted.map((player) => (
          <PlayerRow
            key={player.name}
            player={player}
            isKept={keptName === player.name.toLowerCase()}
            onSelect={() => onSelectPlayer({ teamName, player })}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function ProductionTab({ playerId, pos, name }) {
  const [weeks, setWeeks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadSeasonStats()
      .then((seasonWeeks) => {
        if (!cancelled) {
          setWeeks(seasonWeeks);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const season = getSeason();
  const weekly = useMemo(
    () => getPlayerWeeks(weeks, playerId),
    [weeks, playerId]
  );
  const summary = useMemo(() => summarizePlayer(weekly), [weekly]);

  if (pos === 'DEF') {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Weekly production isn't tracked for D/ST.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3 py-2">
        <div className="grid grid-cols-3 gap-2">
          <Skeleton className="h-12 rounded-md" />
          <Skeleton className="h-12 rounded-md" />
          <Skeleton className="h-12 rounded-md" />
        </div>
        <Skeleton className="h-32 rounded-md" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Couldn't load stats: {error}
      </p>
    );
  }

  if (summary.gp === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No {season} regular-season PPR data for {name}.
      </p>
    );
  }

  const max = Math.max(...weekly.map((w) => w.ppr ?? 0), 1);
  const CHART_PX = 140;
  const LABEL_PX = 16;
  const BAR_AREA = CHART_PX - LABEL_PX;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Games" value={summary.gp} />
        <Stat
          label="Total PPR"
          value={summary.total.toFixed(1)}
          accent
        />
        <Stat label="Per game" value={summary.avg.toFixed(1)} />
      </div>
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
          {season} PPR by week
        </h4>
        <div
          className="flex items-end gap-1 rounded-md border border-border bg-muted/30 p-2"
          style={{ height: `${CHART_PX + 16}px` }}
        >
          {weekly.map((w) => {
            const value = w.ppr ?? 0;
            const zero = value === 0;
            const heightPx = zero
              ? 6
              : Math.max((value / max) * BAR_AREA, 6);
            const label =
              value === 0
                ? '0'
                : value < 10
                ? value.toFixed(1)
                : value.toFixed(0);
            return (
              <Tooltip key={w.week}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Week ${w.week}: ${value.toFixed(1)} PPR`}
                    onClick={(e) => e.stopPropagation()}
                    className="group flex h-full flex-1 cursor-help flex-col items-center justify-end gap-1"
                  >
                    <span
                      className={cn(
                        'text-[9px] font-semibold tabular-nums leading-none',
                        zero ? 'text-muted-foreground' : 'text-foreground'
                      )}
                    >
                      {label}
                    </span>
                    <span
                      style={{ height: `${heightPx}px` }}
                      className={cn(
                        'w-full rounded-sm transition-colors',
                        zero
                          ? 'bg-muted-foreground/25 group-hover:bg-muted-foreground/40'
                          : 'bg-primary/75 group-hover:bg-primary'
                      )}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="font-semibold">Week {w.week}</div>
                  <div>{value.toFixed(1)} PPR</div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        <div className="mt-1 flex justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>Wk 1</span>
          <span>Wk {weekly.length}</span>
        </div>
      </div>
      {summary.best && (
        <p className="text-xs text-muted-foreground">
          Best week: {summary.best.ppr.toFixed(1)} PPR in week{' '}
          {summary.best.week}.
        </p>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-2 text-center">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-0.5 text-base font-bold tabular-nums',
          accent && 'text-primary'
        )}
      >
        {value}
      </div>
    </div>
  );
}

function PlayerHero({ player, teamName, isLocked, TitleComp }) {
  const photo = playerPhotoUrl({ playerId: player.playerId, pos: player.pos });
  return (
    <div className="flex items-start gap-4">
      <TeamRing team={player.team} size="lg">
        <Avatar className="h-full w-full rounded-[11px]">
          {photo && (
            <AvatarImage
              src={photo}
              alt={player.name}
              className={
                player.pos === 'DEF' ? 'object-contain p-3' : undefined
              }
            />
          )}
          <AvatarFallback className="rounded-[11px] text-lg">
            {initials(player.name)}
          </AvatarFallback>
        </Avatar>
      </TeamRing>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <TitleComp className="text-2xl capitalize leading-tight">
            {player.name}
          </TitleComp>
          {getInjury(player) && (
            <InjuryIcon injury={getInjury(player)} size="lg" />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <PositionBadge pos={player.pos} />
          <span className="font-medium text-foreground">{player.team}</span>
          <span>·</span>
          <span>
            {isLocked ? 'kept by' : 'keepable by'} {teamName}
          </span>
        </div>
        <div className="inline-flex items-center gap-1.5 rounded-[6px] border border-primary/40 bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
          <Trophy className="h-3 w-3" />
          Round {player.keep} cost
        </div>
      </div>
    </div>
  );
}

function PlayerTabs({ player }) {
  const desc = getDescription(player.name);
  return (
    <Tabs defaultValue="scouting" className="mt-3">
      <TabsList className="h-auto w-full justify-start gap-4 rounded-none border-b border-border bg-transparent p-0">
        <TabsTrigger
          value="scouting"
          className="rounded-none border-b-2 border-transparent bg-transparent px-0 pb-2 pt-0 text-sm font-semibold text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
        >
          Scouting
        </TabsTrigger>
        <TabsTrigger
          value="production"
          className="rounded-none border-b-2 border-transparent bg-transparent px-0 pb-2 pt-0 text-sm font-semibold text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
        >
          Production
        </TabsTrigger>
      </TabsList>

      <TabsContent value="scouting" className="mt-4 space-y-3">
        {desc && (
          <>
            <p className="text-sm leading-relaxed">{desc.summary}</p>
            {desc.offseason && (
              <section>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">
                  Offseason note
                </h4>
                <p className="text-sm leading-relaxed">{desc.offseason}</p>
              </section>
            )}
            {desc.lastUpdated && (
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Updated {desc.lastUpdated}
                {desc.source === 'web_search_api' &&
                  ' · automatically using web search api'}
              </p>
            )}
          </>
        )}
      </TabsContent>

      <TabsContent value="production" className="mt-4">
        <ProductionTab
          playerId={player.playerId}
          pos={player.pos}
          name={player.name}
        />
      </TabsContent>
    </Tabs>
  );
}

function ExternalLinks({ player }) {
  const googleHref = `https://www.google.com/search?q=${encodeURIComponent(
    `${player.name} fantasy football ${new Date().getFullYear()}`
  )}`;
  const sleeperHref =
    player.playerId && player.pos !== 'DEF'
      ? `https://sleeper.com/nfl/players/${player.playerId}`
      : null;
  return (
    <div className="flex flex-wrap gap-2">
      <a
        href={googleHref}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 rounded-[6px] border border-border bg-muted/50 px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-accent"
      >
        <Search className="h-3 w-3" />
        Google
        <ExternalLink className="h-3 w-3 opacity-60" />
      </a>
      {sleeperHref && (
        <a
          href={sleeperHref}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-[6px] border border-border bg-muted/50 px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-accent"
        >
          Sleeper profile
          <ExternalLink className="h-3 w-3 opacity-60" />
        </a>
      )}
    </div>
  );
}

function PlayerActions({ onClose, onLockIn, locking, full }) {
  return (
    <>
      <Button
        variant="outline"
        onClick={onClose}
        disabled={locking}
        className={full ? 'flex-1' : undefined}
      >
        Cancel
      </Button>
      <Button
        onClick={onLockIn}
        disabled={locking}
        className={cn('gap-2', full && 'flex-1')}
      >
        <Lock className="h-4 w-4" />
        {locking ? 'Locking in…' : 'Lock in as keeper'}
      </Button>
    </>
  );
}

function PlayerOverlay({ selection, isLocked, onClose, onLockIn, locking }) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const open = Boolean(selection);
  const handleOpenChange = (o) => {
    if (!o) onClose();
  };
  const player = selection?.player;
  const teamName = selection?.teamName;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerContent>
          {player && (
            <>
              <DrawerHeader>
                <PlayerHero
                  player={player}
                  teamName={teamName}
                  isLocked={isLocked}
                  TitleComp={DrawerTitle}
                />
              </DrawerHeader>
              <DrawerBody className="space-y-3 pb-4">
                <PlayerTabs player={player} />
                <ExternalLinks player={player} />
              </DrawerBody>
              <DrawerFooter>
                <div className="flex w-full gap-2">
                  <PlayerActions
                    onClose={onClose}
                    onLockIn={onLockIn}
                    locking={locking}
                    full
                  />
                </div>
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        {player && (
          <>
            <DialogHeader>
              <PlayerHero
                player={player}
                teamName={teamName}
                isLocked={isLocked}
                TitleComp={DialogTitle}
              />
            </DialogHeader>
            <PlayerTabs player={player} />
            <div className="mt-3">
              <ExternalLinks player={player} />
            </div>
            <DialogFooter className="mt-2 gap-2">
              <PlayerActions
                onClose={onClose}
                onLockIn={onLockIn}
                locking={locking}
              />
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function App() {
  const [kept, setKept] = useState({});
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [draftSet, setDraftSet] = useState(false);
  const [selection, setSelection] = useState(null);
  const [locking, setLocking] = useState(false);

  useEffect(() => {
    const getPlayerKeeperValue = ({ id, draft }) => {
      const drafted = draft.find((x) => x.player_id === id);
      return drafted?.round ?? 6;
    };

    const buildTeams = ({ teams, players, users, draft }) => {
      return teams.reduce((acc, curr) => {
        const p = curr.players.reduce((a, c) => {
          const meta = players[c];
          if (!meta) return a;
          const name =
            meta.position === 'DEF'
              ? `${meta.first_name} ${meta.last_name} D/ST`
              : meta.full_name;
          return {
            ...a,
            [name]: {
              playerId: c,
              keep: getPlayerKeeperValue({ draft, id: c }),
              name,
              pos: meta.position,
              team: meta.team ?? 'FA',
              injuryStatus: meta.injury_status ?? null,
              injuryBodyPart: meta.injury_body_part ?? null,
              injuryNotes: meta.injury_notes ?? null,
              injuryStartDate: meta.injury_start_date ?? null,
              status: meta.status ?? null,
            },
          };
        }, {});
        const usr = users.find((x) => x.user_id === curr.owner_id);
        const teamName = usr.metadata?.team_name ?? usr.display_name;
        return { ...acc, [teamName]: p };
      }, {});
    };

    const setData = async () => {
      const [players, teamRes, users, draftInfo, nextDraft] = await Promise.all([
        fetch('https://api.sleeper.app/v1/players/nfl').then((x) => x.json()),
        fetch(
          `https://api.sleeper.app/v1/league/${process.env.REACT_APP_LAST_YEAR_LEAGUE_ID}/rosters`
        ).then((x) => x.json()),
        fetch(
          `https://api.sleeper.app/v1/league/${process.env.REACT_APP_LAST_YEAR_LEAGUE_ID}/users`
        ).then((x) => x.json()),
        fetch(
          `https://api.sleeper.app/v1/league/${process.env.REACT_APP_LAST_YEAR_LEAGUE_ID}/drafts`
        ).then((x) => x.json()),
        fetch(
          `https://api.sleeper.app/v1/league/${process.env.REACT_APP_THIS_YEAR_LEAGUE_ID}/drafts`
        ).then((x) => x.json()),
      ]);

      setDraftSet(nextDraft[0].start_time);

      const draft = await fetch(
        `https://api.sleeper.app/v1/draft/${draftInfo[0].draft_id}/picks`
      ).then((x) => x.json());
      const built = buildTeams({ teams: teamRes, players, users, draft });

      setTeams(built);
      setLoading(false);
    };

    setData();

    axios(`${SERVER_URL}/api/all`).then((res) => {
      setKept(
        res.data.data.reduce((acc, curr) => {
          return {
            ...acc,
            [curr.user]: { keeper: curr.keeper, cost: curr.cost },
          };
        }, {})
      );
    });
  }, []);

  const handleSelectPlayer = useCallback(({ teamName, player }) => {
    setSelection({ teamName, player });
  }, []);

  const handleClose = useCallback(() => {
    if (locking) return;
    setSelection(null);
  }, [locking]);

  const handleLockIn = useCallback(async () => {
    if (!selection) return;
    setLocking(true);
    try {
      await axios.post(
        `${SERVER_URL}/api/save?user=${selection.teamName}&keeper=${selection.player.name.toLowerCase()}&cost=${selection.player.keep}`
      );
      setKept((prev) => ({
        ...prev,
        [selection.teamName]: {
          ...(prev[selection.teamName] ?? {}),
          keeper: selection.player.name.toLowerCase(),
          cost: selection.player.keep,
        },
      }));
      setSelection(null);
    } finally {
      setLocking(false);
    }
  }, [selection]);

  const visibleTeams = useMemo(() => {
    return Object.entries(teams).filter(
      ([teamName]) => !EXCLUDED_TEAMS.has(teamName)
    );
  }, [teams]);

  const draftInFuture =
    draftSet && new Date(draftSet).getTime() > Date.now();

  return (
    <TooltipProvider delayDuration={150}>
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-background/40 backdrop-blur-2xl backdrop-saturate-150">
        <div className="mx-auto flex max-w-[1760px] flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div>
            <h1 className="text-lg font-bold tracking-tight text-primary sm:text-xl">
              Fam Fantasy Keepers
            </h1>
            <p className="text-xs text-muted-foreground">
              Pick your keeper before the draft drops.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {draftInFuture ? (
              <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-primary">
                <span className="text-xs font-semibold uppercase tracking-wide">
                  Draft in
                </span>
                <span className="text-base font-bold tabular-nums">
                  <Countdown date={draftSet} />
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-muted-foreground">
                <span className="text-xs font-semibold uppercase tracking-wide">
                  Draft date:
                </span>
                <span className="text-sm font-bold uppercase tracking-wide">
                  Not set
                </span>
              </div>
            )}
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1760px] px-4 py-6">
        {loading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <TeamCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {visibleTeams.map(([teamName, players]) => (
              <TeamCard
                key={teamName}
                teamName={teamName}
                players={Object.values(players)}
                kept={kept}
                onSelectPlayer={handleSelectPlayer}
              />
            ))}
          </div>
        )}
      </main>

      <PlayerOverlay
        selection={selection}
        isLocked={
          selection
            ? kept[selection.teamName]?.keeper ===
              selection.player.name.toLowerCase()
            : false
        }
        onClose={handleClose}
        onLockIn={handleLockIn}
        locking={locking}
      />
    </div>
    </TooltipProvider>
  );
}

export default App;
